import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import VendorCard from "@/components/VendorCard";
import { CATEGORIES } from "@/lib/categories";

const CATS = CATEGORIES.map((c, i) => ({ ...c, tag: String(i + 1).padStart(2, "0") }));

const HERO_ROTATION = [
  { key: "weddings",  label: "Weddings",        image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=85" },
  { key: "corporate", label: "Corporate events", image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1600&q=85" },
  { key: "private",   label: "Private parties",  image: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1600&q=85" },
  { key: "dining",    label: "Fine dining",      image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=1600&q=85" },
  { key: "outdoor",   label: "Outdoor events",   image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1600&q=85" },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => { api.get("/vendors/featured").then(({ data }) => setFeatured(data)).catch(() => {}); }, []);
  useEffect(() => {
    // preload rotation images for smooth crossfade
    HERO_ROTATION.forEach((h) => { const i = new Image(); i.src = h.image; });
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_ROTATION.length), 4500);
    return () => clearInterval(t);
  }, []);
  const heroCurrent = HERO_ROTATION[heroIdx];

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 pt-10 sm:pt-16 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-8 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">A Curated Marketplace</div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="font-serif text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[6.5rem] leading-[0.95] tracking-tight"
              data-testid="hero-title"
            >
              Compose <em className="italic font-light">unforgettable</em><br />events, effortlessly.
            </motion.h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[#5C5C5C] max-w-xl leading-relaxed" data-testid="hero-subtitle">
              NContro connects discerning planners with a hand-picked collective of caterers, restaurants, venues, florists and DJs — all in one place.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
              <Link to="/browse" data-testid="hero-browse-btn">
                <Button className="btn-sage rounded-full h-12 px-6 sm:px-8 text-sm">
                  Browse the collective <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-vendor-btn" className="text-sm link-underline">
                Are you a vendor? Join NContro →
              </Link>
            </div>

            <div className="mt-12 sm:mt-16 grid grid-cols-3 max-w-md gap-4 sm:gap-8">
              <Stat n="240+" l="Vetted vendors" />
              <Stat n="18" l="Cities" />
              <Stat n="4.9" l="Avg. rating" />
            </div>
          </div>

          <div className="lg:col-span-5 relative aspect-[4/5] sm:aspect-[16/10] lg:aspect-auto lg:h-[620px] rounded-md overflow-hidden bg-[color:var(--surface)]" data-testid="hero-rotation">
            <AnimatePresence mode="sync">
              <motion.img
                key={heroCurrent.key}
                src={heroCurrent.image}
                alt={heroCurrent.label}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 0.9 }, scale: { duration: 6, ease: "linear" } }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>

            {/* subtle gradient for label legibility */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />

            {/* Category label */}
            <div className="absolute left-5 bottom-5 sm:left-6 sm:bottom-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={heroCurrent.key + "-label"}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mb-1">Now showing</div>
                  <div className="font-serif text-2xl sm:text-3xl text-white leading-none">{heroCurrent.label}</div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dot indicators */}
            <div className="absolute right-5 bottom-5 sm:right-6 sm:bottom-6 flex gap-2">
              {HERO_ROTATION.map((h, i) => (
                <button
                  key={h.key}
                  data-testid={`hero-dot-${h.key}`}
                  aria-label={`Show ${h.label}`}
                  onClick={() => setHeroIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === heroIdx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY STRIP */}
      <section className="border-y border-[#E8E2D9] bg-[#F5F2EB]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
          <div className="eyebrow">Explore by discipline</div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {CATS.map((c) => (
              <Link key={c.key} to={`/browse?category=${c.key}`} data-testid={`cat-${c.key}`} className="chip">
                <span className="text-[10px] mr-2 opacity-50">{c.tag}</span> {c.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-8 sm:mb-10">
          <div>
            <div className="eyebrow mb-3">Currently featured</div>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight tracking-tight max-w-2xl">
              The <em className="italic">NContro</em> collective
            </h2>
          </div>
          <Link to="/browse" data-testid="see-all-link" className="text-sm link-underline hidden sm:inline">See all vendors →</Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((v) => (
            <VendorCard key={v.vendor_id} v={v} />
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F5F2EB] border-t border-[#E8E2D9]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">The Method</div>
              <h2 className="font-serif text-3xl sm:text-5xl leading-tight">Every event, orchestrated.</h2>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6 sm:gap-8">
              {[
                ["01", "Discover", "Explore a curated network of vendors, filtered by discipline, city and budget."],
                ["02", "Compose", "Create an event and gather your dream team into a single elegant plan."],
                ["03", "Confirm", "Message vendors, receive quotes, and secure your date with a simple deposit."],
              ].map(([n, t, d]) => (
                <div key={n} data-testid={`step-${n}`} className="border-t border-[#E8E2D9] pt-6">
                  <div className="eyebrow">{n}</div>
                  <h3 className="font-serif text-2xl mt-3">{t}</h3>
                  <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 text-center">
        <div className="eyebrow mb-4">Ready to begin?</div>
        <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-none">
          Your next great event <em className="italic">starts here.</em>
        </h2>
        <div className="mt-8 sm:mt-10">
          <Link to="/register" data-testid="cta-signup">
            <Button className="btn-sage rounded-full h-14 px-8 sm:px-10 text-sm">Create your NContro account</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-2xl sm:text-3xl">{n}</div>
      <div className="eyebrow mt-1 text-[0.6rem] sm:text-[0.7rem]">{l}</div>
    </div>
  );
}
